import { useMemo } from "react";
import PageTemplate from "../../components/ui/common/template/PageTemplate";
import CenterDiv from "../../style/styled-components/components/CenterDiv";
import ContentDiv from "../../style/styled-components/components/ContentDiv";
import ReactMarkdown from "react-markdown";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import { errormessages } from "../../context/languages/kr/errormessages";
import remarkGfm from "remark-gfm";
import DOMPurify from "dompurify";
import { useArticle, useArticles } from "../../components/hooks/useArticles";
const ArticlePage = ({ category = "", title = "게시글 읽기" }) => {
  const { id } = useParams();
  const no = Number(id);
  const list = useArticles(category);      // 이전/다음 글 계산용 목록
  const article = useArticle(category, no); // 본문 + 첨부 (DB)
  const content = article === null ? "## 등록된 글이 없습니다." : article?.content || "";
  const refUrl = useMemo(() => {
    try {
      const url = new URL(article?.ref_url);
      return ["http:", "https:"].includes(url.protocol) ? url.href : null;
    } catch { return null; }
  }, [article?.ref_url]);
  const openReference = event => {
    if (!refUrl || window.getSelection()?.toString()) return;
    event.preventDefault(); // 본문 안의 링크를 눌러도 창은 한 번만 연다.
    window.open(refUrl, "_blank", "popup,width=1200,height=900,noopener,noreferrer");
  };
  const getArticle = (array, no) => array.find(x => x.no === no);
  const { title: currentTitle, subtitle: currentSubTitle = "", attached: currentAttacheds, date: currentDate } = article || {};
  const prev = useMemo(() => Math.max(...list.filter(x => x.no < no).map(x => x.no)), [no, list]);
  const next = useMemo(() => Math.min(...list.filter(x => x.no > no).map(x => x.no)), [no, list]);
  return (
    <PageTemplate title={title} noImage>
      <CenterDiv>
        <ContentDiv>
          <Div>
            <h1>{currentTitle}</h1>
            {currentSubTitle &&
              <h3>[{currentSubTitle}]</h3>
            }
            <div className="postDate">{currentDate}</div>
            <hr />
            {/* {refUrl && <p className="referenceNotice">본문을 클릭하면 원문이 새 창으로 열립니다.</p>} */}
            <div className="body"
              role={refUrl ? "link" : undefined}
              tabIndex={refUrl ? 0 : undefined}
              aria-label={refUrl ? "원문 새 창으로 열기" : undefined}
              onClick={refUrl ? openReference : undefined}
              onKeyDown={refUrl ? event => {
                if (event.key === "Enter" && event.target === event.currentTarget) openReference(event);
              } : undefined}>
              {/^\s*</.test(content)  // 에디터(homepage_backend/editor.js)로 쓴 글은 HTML, 이전 글은 Markdown
                ? <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content, { ADD_ATTR: ["target"] }) }} />
                : <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>}
            </div>
            {
              currentAttacheds?.length > 0 && (
                <>
                  <div className="attachedTitle">
                    첨부파일 다운로드
                  </div>
                  <div className="attached">
                    {
                      currentAttacheds.map((x, idx) => <a key={idx} href={x.url} download target="_blank" rel="noreferrer">{x.filename}</a>)
                    }
                  </div>
                </>
              )
            }
            <hr />
            <div className="prevnext">
              {
                <Link to={`/boards/${category}/${prev}`} disabled={!isFinite(prev)}><div className="prev"><div>← 이전</div><div>{isFinite(prev) ? getArticle(list, prev)?.title : errormessages.NO_PREVIOUS_ARTICLE}</div></div></Link>
              }
              {
                <Link to={`/boards/${category}/${next}`} disabled={!isFinite(next)}><div className="next"><div>다음 →</div><div>{isFinite(next) ? getArticle(list, next)?.title : errormessages.NO_NEXT_ARTICLE}</div></div></Link>
              }
            </div>
          </Div>
        </ContentDiv>
      </CenterDiv>
    </PageTemplate>
  );
};
export default ArticlePage;
// ponytail: 화면만 리디자인. 본문·첨부·이전/다음 글 로직(API)은 그대로
const Div = styled.div`
padding-top: 3.5rem;
& blockquote {
  margin: 1.5rem 0;
  padding: 1rem 1.5rem;
  border-left: 4px solid ${({ theme }) => theme?.colors?.accent};
  border-radius: 0 0.6rem 0.6rem 0;
  background-color: ${({ theme }) => theme?.colors?.monotone?.extralight};
}
& img {
  max-width: 100%;
  margin-left: auto;
  border-radius: 0.5rem;
}
& .body {
  &[role="link"] {
    cursor: pointer;
    &:focus-visible { outline: 2px solid currentColor; outline-offset: 4px; }
  }
  a {
    color: ${({ theme }) => theme?.colors?.accent};
    text-decoration: underline;
    text-underline-offset: .3em;
  }
}
& p {
  text-align: left;
  font-size: 1.05rem;
  line-height: 1.85;
  color: ${({ theme }) => theme?.colors?.monotone?.dark};
}
& li {
  line-height: 1.8;
  color: ${({ theme }) => theme?.colors?.monotone?.dark};
}
& h1 {
  margin: 0 0 0.75rem;
  font-size: clamp(1.5rem, 2.6vw, 2.1rem);
  line-height: 1.4;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme?.colors?.dark};
}
& table {
  font-size: .9em;
  display: block;
  max-width: 100%;
  max-width: -moz-fit-content;
  max-width: fit-content;
  margin: 1.5rem auto;
  overflow-x: auto;
  white-space: nowrap;
  border-top: 2px solid ${({ theme }) => theme?.colors?.dark};
    & th {
      background-color: ${({ theme }) => theme?.colors?.extralight};
      font-weight: 600;
      padding: .6rem 1rem;
      border-bottom: 1px solid ${({ theme }) => theme?.colors?.line};
    }
    & td {
      padding: .5rem 1rem;
      border-bottom: 1px solid ${({ theme }) => theme?.colors?.line};
    }
  }
  & > h3 {
    margin: 0 0 0.75rem;
    color: ${({ theme }) => theme?.colors?.foreground?.dimmed};
    font-weight: 500;
  }
  & > div.prevnext {
    margin-top: 3rem;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 1rem;
    @media (max-width: 575px) {
      grid-template-columns: minmax(0, 1fr);
    }
    & > a {
      display: block;
      padding: 1.1rem 1.25rem;
      border-radius: 0.875rem;
      border: 1px solid ${({ theme }) => theme?.colors?.line};
      transition: border-color 0.15s, background-color 0.15s;
      &:hover {
        border-color: ${({ theme }) => theme?.colors?.light};
        background-color: ${({ theme }) => theme?.colors?.monotone?.extralight};
      }
      &[disabled] {
        pointer-events: none;
        color: ${({ theme }) => theme?.colors?.foreground?.dimmed};
        background-color: ${({ theme }) => theme?.colors?.monotone?.extralight};
      }
      & > div {
        display: flex;
        flex-direction: column;
        gap: 0.35rem;
        & > div:first-child {
          font-size: 0.85rem;
          font-weight: 700;
          color: ${({ theme }) => theme?.colors?.accent};
        }
        & > div:last-child {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        &.next {
          align-items: flex-end;
          text-align: right;
          & > div:last-child {
            max-width: 100%;
          }
        }
      }
    }
  }
  & hr {
    margin: 2rem 0;
    border: 0;
    border-top: 1px solid ${({ theme }) => theme?.colors?.line};
  }
  & > div.attached {
    display: flex;
    flex-direction: column;
    gap: .4rem;
    padding: 0.75rem 1.25rem 1.25rem;
    border: 1px solid ${({ theme }) => theme?.colors?.line};
    border-top: 0;
    border-radius: 0 0 0.875rem 0.875rem;
    & > a {
      color: ${({ theme }) => theme?.colors?.accent};
      word-break: break-all;
      &:before {
        content: "↓ ";
        font-weight: 700;
      }
      &:hover {
        text-decoration: underline;
      }
    }
  }
  & > div.attachedTitle {
    margin-top: 2.5rem;
    color: ${({ theme }) => theme?.colors?.foreground?.highlight};
    font-weight: 700;
    padding: 0.9rem 1.25rem;
    border-radius: 0.875rem 0.875rem 0 0;
    border: 1px solid ${({ theme }) => theme?.colors?.line};
    background-color: ${({ theme }) => theme?.colors?.monotone?.extralight};
  }
  .postDate {
    color: ${({ theme }) => theme?.colors?.foreground?.dimmed};
    font-size: 0.95rem;
  }
`;
